/**
 * Crypto utilities for key management
 * Platform-agnostic - works in both browser and React Native
 */

const KEY_STORAGE_NAME = 'myagent_private_key'

/**
 * Generate random bytes
 * @param {number} length - Number of bytes
 * @returns {Uint8Array}
 */
const randomBytes = (length = 32) => {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    // Browser and modern environments
    return crypto.getRandomValues(new Uint8Array(length))
  } else {
    // Fallback (should not happen in modern browsers/RN)
    throw new Error('crypto.getRandomValues not available')
  }
}

/**
 * Convert bytes to hex string
 * @param {Uint8Array} bytes
 * @returns {string}
 */
export const bytesToHex = (bytes) => {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Convert hex string to bytes
 * @param {string} hex
 * @returns {Uint8Array}
 */
export const hexToBytes = (hex) => {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16)
  }
  return bytes
}

/**
 * Storage adapter - allows different storage backends
 */
let storageAdapter = {
  getItem: (key) => {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(key)
    }
    return null
  },
  setItem: (key, value) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value)
    }
  },
  removeItem: (key) => {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key)
    }
  },
}

/**
 * Configure storage adapter
 * Use this for React Native AsyncStorage or other storage backends
 * @param {Object} adapter - Storage adapter with getItem, setItem, removeItem methods
 */
export const configureStorage = (adapter) => {
  storageAdapter = adapter
}

/**
 * Get or generate private key
 * Stored in configured storage for persistence across sessions
 * @returns {string} Hex string of private key
 */
export const getPrivateKey = () => {
  const existing = storageAdapter.getItem(KEY_STORAGE_NAME)
  if (!existing) {
    const bytes = randomBytes(32)
    const hex = bytesToHex(bytes)
    storageAdapter.setItem(KEY_STORAGE_NAME, hex)
    console.log('[Crypto] Generated new private key')
    return hex
  }
  return existing
}

/**
 * Get public key from client instance in hex format
 * @param {Object} client - Client instance with publicKey property
 * @returns {string} Public key hex string (64 characters)
 */
export const getPublicKey = (client) => {
  if (!client) {
    return 'not_connected'
  }

  const publicKey = client.publicKey
  if (!publicKey) {
    return 'connecting...'
  }

  // Convert Uint8Array to hex string
  if (publicKey instanceof Uint8Array) {
    return bytesToHex(publicKey)
  }

  // If it's already a string and looks like base64, convert to hex
  if (typeof publicKey === 'string') {
    if (publicKey.includes('+') || publicKey.includes('/') || publicKey.includes('=')) {
      try {
        const bytes = Uint8Array.from(atob(publicKey), c => c.charCodeAt(0))
        return bytesToHex(bytes)
      } catch (e) {
        console.warn('[Crypto] Failed to convert public key to hex:', e)
        return publicKey
      }
    }
    return publicKey.toLowerCase()
  }

  return String(publicKey)
}

/**
 * Reset key (clear from storage)
 * Note: Application should handle reload/restart after this
 * @returns {boolean} Success
 */
export const resetKey = () => {
  try {
    storageAdapter.removeItem(KEY_STORAGE_NAME)
    console.log('[Crypto] Key reset successfully')
    return true
  } catch (error) {
    console.error('[Crypto] Failed to reset key:', error)
    return false
  }
}

/**
 * Copy text to clipboard
 * @param {string} text
 * @returns {Promise<boolean>} Success
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text)
      console.log('[Crypto] Copied to clipboard')
      return true
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      const success = document.execCommand('copy')
      document.body.removeChild(textarea)
      return success
    }
  } catch (error) {
    console.error('[Crypto] Failed to copy:', error)
    return false
  }
}
